import { tr } from '@/contexts/LanguageContext';
/**
 * Media upload client — uses XHR (not fetch) so we get real progress events
 * for the Instagram-style top progress bar.
 *
 *   uploadsApi.uploadMedia(file, { onProgress: (pct) => ... })
 *     → { url, kind: 'image'|'video', size_bytes, ... }
 */
const BACKEND_HOST = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'lineage_token';

export const MAX_IMAGE_MB = 30;        // matches backend MAX_IMAGE_BYTES
export const MAX_VIDEO_MB = 500;       // matches backend MAX_VIDEO_BYTES + Cloudflare edge cap
// Iter 165.1 · Voice-note upload cap. Mirrors backend `MAX_AUDIO_BYTES`
// (10 MB) which is a huge safety margin above the 60 s @ opus ceiling.
export const MAX_AUDIO_MB = 10;
// Above this, the picker triggers a "this might take a while" toast so the
// user understands the long upload time isn't a hang.
export const HEAVY_VIDEO_MB = 100;

const ALLOWED_IMAGE = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif',
]);
const ALLOWED_VIDEO = new Set([
  'video/mp4', 'video/quicktime', 'video/webm',
]);
// Iter 165.1 · Audio MIME types. MediaRecorder emits:
//   · Chromium / Firefox → audio/webm (opus)
//   · Safari (iOS / macOS) → audio/mp4 (aac). Some Safari builds also
//     surface `audio/x-m4a` or `audio/m4a` for the same stream.
//   · Older Firefox / Opera → audio/ogg
// We accept the full set so the composer works regardless of the
// browser without server-side transcoding.
const ALLOWED_AUDIO = new Set([
  'audio/webm',
  'audio/ogg',
  'audio/mp4',
  'audio/x-m4a',
  'audio/m4a',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/aac',
]);

// Extensions that unambiguously map to audio when the browser hands us
// a blank or `application/octet-stream` MIME (happens with some Safari
// MediaRecorder builds).
const AUDIO_EXTENSIONS = new Set([
  'webm', 'ogg', 'oga', 'm4a', 'mp4', 'mp3', 'wav', 'aac',
]);

// Sprint 5 · P0-1/P0-2 — Android entrega archivos content:// (Google
// Photos, Files) con MIME vacío. Fallback por extensión para imagen/video
// cuando el caller lo pide con { prefer: 'visual' } (posts/stories/avatar).
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'm4v', 'webm', '3gp', '3g2']);

export class UploadError extends Error {
  constructor(message, { status, sizeMB } = {}) {
    super(message);
    this.name = 'UploadError';
    this.status = status;
    this.sizeMB = sizeMB;
  }
}

const _fileExt = (file) => {
  const name = (file?.name || '').toLowerCase();
  if (!name.includes('.')) return '';
  return name.split('.').pop().split('?')[0];
};

export const classifyFile = (file, { prefer } = {}) => {
  const t = (file?.type || '').toLowerCase().split(';')[0].trim();
  if (ALLOWED_IMAGE.has(t)) return 'image';
  if (ALLOWED_VIDEO.has(t)) return 'video';
  if (ALLOWED_AUDIO.has(t)) return 'audio';
  if (t === '' || t === 'application/octet-stream') {
    const ext = _fileExt(file);
    // Visual pickers (posts/stories/avatar): image/video first — Android
    // content:// files often report an empty MIME.
    if (prefer === 'visual') {
      if (IMAGE_EXTENSIONS.has(ext)) return 'image';
      if (VIDEO_EXTENSIONS.has(ext)) return 'video';
    }
    // Voice-note composer always names the File with a safe extension.
    if (AUDIO_EXTENSIONS.has(ext)) return 'audio';
    if (IMAGE_EXTENSIONS.has(ext)) return 'image';
    if (VIDEO_EXTENSIONS.has(ext)) return 'video';
  }
  return null;
};

export const validateFile = (file, opts = {}) => {
  if (!file) throw new UploadError(tr("Selecciona un archivo."));
  const kind = classifyFile(file, opts);
  if (!kind) {
    throw new UploadError(
      tr("Formato no soportado. Usa imágenes (jpg, png, webp, gif, heic), videos (mp4, mov, webm) o audios (webm, m4a, mp3, ogg, wav, aac)."),
    );
  }
  const maxMB = kind === 'image' ? MAX_IMAGE_MB
              : kind === 'video' ? MAX_VIDEO_MB
              : MAX_AUDIO_MB;
  const sizeMB = Math.round(file.size / (1024 * 1024));
  if (file.size > maxMB * 1024 * 1024) {
    if (kind === 'video') {
      throw new UploadError(
        tr("Este video supera el límite de {0} MB para Stories.", maxMB),
        { sizeMB },
      );
    }
    if (kind === 'audio') {
      throw new UploadError(
        tr("Esta nota de voz supera el máximo de {0} MB.", maxMB),
        { sizeMB },
      );
    }
    throw new UploadError(tr("El archivo supera el máximo de {0} MB.", maxMB), { sizeMB });
  }
  return kind;
};

/** True when a picked video is heavy enough to warrant the "may take a few
 *  minutes" toast — used by the upload dialog before kicking off XHR. */
export const isHeavyVideo = (file) => {
  if (!file) return false;
  if (classifyFile(file) !== 'video') return false;
  return file.size > HEAVY_VIDEO_MB * 1024 * 1024;
};

export const uploadsApi = {
  uploadMedia(file, { onProgress, signal, maxSeconds } = {}) {
    const q = maxSeconds ? `?max_seconds=${Number(maxSeconds)}` : '';
    return _xhrUpload(file, `/api/uploads/media${q}`, { onProgress, signal });
  },

  /**
   * Iter 112 — Dedicated, permanent endpoint for profile photos.
   *
   * Hits `/api/uploads/avatar` which writes to
   *   `lineage-barber-world/uploads/avatars/{user_id}/{file_id}.{ext}`
   * — fully isolated from the monthly post/reel/story bucket. The
   * stories_cleanup loop has an explicit `/avatars/` exclusion so the
   * file is permanent until the user uploads a new one or explicitly
   * removes their photo.
   *
   * Behaves like uploadMedia (XHR with progress + i18n header), just on
   * a different URL with a tighter validateFile (images only).
   */
  uploadAvatar(file, { onProgress, signal } = {}) {
    return _xhrUpload(file, '/api/uploads/avatar', { onProgress, signal });
  },
};

/** Shared XHR upload helper. Identical wire shape for media + avatar —
 *  only the URL changes. Keeps both endpoints honest to the same
 *  i18n / progress / error contract. */
function _xhrUpload(file, path, { onProgress, signal } = {}) {
  const kind = validateFile(file);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = `${BACKEND_HOST}${path}`;
    xhr.open('POST', url, true);
    const token = (typeof localStorage !== 'undefined' && localStorage.getItem(TOKEN_KEY)) || '';
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    // Iter 111 — i18n header so the strict 503 from the backend
    // (Iter 107) lands in the barber's preferred language.
    try {
      const langRaw = (localStorage.getItem('lineage_lang') || '').toLowerCase().split(/[-_]/)[0];
      const lang = ['en','es','pt','fr'].includes(langRaw) ? langRaw : 'es';
      xhr.setRequestHeader('Accept-Language', lang);
    } catch { /* private mode */ }

    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        onProgress(Math.round((e.loaded / e.total) * 95));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (typeof onProgress === 'function') onProgress(100);
          resolve({ ...data, _detectedKind: kind });
        } catch {
          reject(new UploadError(tr("Respuesta inválida del servidor"), { status: xhr.status }));
        }
      } else {
        let detail = tr("La subida falló. Inténtalo de nuevo.");
        try {
          const body = JSON.parse(xhr.responseText);
          detail = body?.detail || detail;
        } catch { /* keep generic */ }
        reject(new UploadError(detail, { status: xhr.status }));
      }
    };
    xhr.onerror = () => reject(new UploadError(tr("Error de red al subir el archivo")));
    xhr.onabort = () => reject(new UploadError(tr("Subida cancelada")));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort());
    }

    const form = new FormData();
    form.append('file', file);
    xhr.send(form);
  });
}

export default uploadsApi;
